import { useEffect, useMemo, useRef, useState } from "react";
import { Alert, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Typography } from "@mui/material";
import "../../design-system/hpoint/table-tennis.css";
import { useLocation } from "react-router-dom";
import { HttpMainApi } from "../../interface/main-api";
import { userState } from "../../interface/MainInterface";

interface SlotReservation {
  reservationId: string;
  reservDate: string;
  slot: string;
  reservState: "신청" | "취소";
  communityPrice: number;
}
interface ReservationGroup {
  reservationId: string;
  reservDate: string;
  slots: SlotReservation[];
}
interface CreatedReservation { userId: string; reservationId: string; slots: string[] }

const mainApi = new HttpMainApi();
const addHour = (slot: string) => `${Number(slot.slice(0, 2)) + 1}:00`;
const dateLabel = (date: string) => `${date.slice(0, 4)}년 ${date.slice(4, 6)}월 ${date.slice(6, 8)}일`;
const startMs = (date: string, slot: string) => Date.parse(
  `${date.slice(0, 4)}-${date.slice(4, 6)}-${date.slice(6, 8)}T${slot}:00+09:00`,
);
const timeRange = (rows: SlotReservation[]) => {
  const ranges: string[] = [];
  rows.forEach((row, index) => {
    if (index === 0 || addHour(rows[index - 1].slot) !== row.slot) ranges.push(row.slot);
    if (index === rows.length - 1 || addHour(row.slot) !== rows[index + 1].slot) {
      ranges[ranges.length - 1] += ` — ${addHour(row.slot)}`;
    }
  });
  return ranges.join(" · ");
};
const validSlot = (slot: unknown): slot is string => typeof slot === "string" && /^(1[0-9]|2[01]):00$/.test(slot);
const validRow = (row: any): row is SlotReservation => !!row && typeof row.reservationId === "string"
  && !!row.reservationId && typeof row.reservDate === "string" && /^\d{8}$/.test(row.reservDate)
  && validSlot(row.slot) && Number.isFinite(startMs(row.reservDate, row.slot))
  && ["신청", "취소"].includes(row.reservState)
  && Number.isSafeInteger(Number(row.communityPrice ?? 0)) && Number(row.communityPrice ?? 0) >= 0;
const totalPrice = (rows: SlotReservation[]) => rows.reduce((sum, row) => sum + Number(row.communityPrice ?? 0), 0).toLocaleString("ko-KR");
const cancelableSuffix = (group: ReservationGroup, now: number) => {
  const active = group.slots.filter(row => row.reservState === "신청");
  const suffix: string[] = [];
  for (let index = active.length - 1; index >= 0; index--) {
    const row = active[index];
    if (startMs(group.reservDate, row.slot) <= now
      || (suffix.length > 0 && addHour(row.slot) !== suffix[0])) break;
    suffix.unshift(row.slot);
  }
  return suffix;
};

const ReservationHistory = ({ userId }: { userId: string }) => {
  const location = useLocation();
  const candidate = location.state?.createdReservation as CreatedReservation | undefined;
  const created = candidate?.userId === userId && typeof candidate.reservationId === "string"
    && candidate.reservationId && Array.isArray(candidate.slots) && candidate.slots.length > 0
    && candidate.slots.every(validSlot) ? candidate : null;
  const [rows, setRows] = useState<SlotReservation[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [reload, setReload] = useState(0);
  const createdFound = useRef(false);
  const [selection, setSelection] = useState<{ reservationId: string; slots: string[] } | null>(null);
  const [expandedId, setExpandedId] = useState("");
  const [confirmingId, setConfirmingId] = useState("");
  const [cancelling, setCancelling] = useState(false);
  const [notice, setNotice] = useState<{ severity: "success" | "error"; text: string } | null>(null);
  const [now, setNow] = useState(0);
  const clock = useRef({ serverMs: 0, receivedAt: 0 });
  const mounted = useRef(true);
  const cancelInFlight = useRef(false);
  // A successful cancellation is authoritative even while the GSI still returns the old state.
  const confirmedCancellations = useRef(new Map<string, Set<string>>());
  const currentTime = () => clock.current.serverMs + Date.now() - clock.current.receivedAt;

  useEffect(() => {
    mounted.current = true;
    const timer = window.setInterval(() => {
      if (clock.current.serverMs) setNow(currentTime());
    }, 1000);
    return () => { mounted.current = false; window.clearInterval(timer); };
  }, []);

  const expectedId = created?.reservationId;
  const expectedSlots = created?.slots.join(",") ?? "";
  useEffect(() => {
    let active = true;
    let retryTimer: number | undefined;
    const load = async (attempt: number) => {
      setLoading(true);
      setSelection(null);
      setExpandedId("");
      setConfirmingId("");
      try {
        const res = await mainApi.get_my_table_tennis_reservations({ userId });
        if (!active) return;
        if (res.code !== "200" || !Array.isArray(res.response?.result)
          || !res.response.result.every(validRow) || typeof res.response.serverNow !== "string"
          || !Number.isFinite(Date.parse(res.response.serverNow))) throw new Error("Invalid history");
        const result: SlotReservation[] = res.response.result;
        clock.current = { serverMs: Date.parse(res.response.serverNow), receivedAt: Date.now() };
        setNow(clock.current.serverMs);
        setRows(result.map(row => confirmedCancellations.current.get(row.reservationId)?.has(row.slot)
          ? { ...row, reservState: "취소", communityPrice: 0 } : row));
        setLoadError(false);
        const found = !!expectedId && expectedSlots.split(",").every(slot =>
          result.some(row => row.reservationId === expectedId && row.slot === slot));
        if (found) createdFound.current = true;
        if (expectedId && !found && !createdFound.current && attempt < 2) {
          retryTimer = window.setTimeout(() => void load(attempt + 1), 1500);
        }
      } catch {
        if (active) setLoadError(true);
      } finally {
        if (active) setLoading(false);
      }
    };
    void load(0);
    return () => { active = false; window.clearTimeout(retryTimer); };
  }, [userId, reload, expectedId, expectedSlots]);

  const groups = useMemo(() => {
    const grouped = new Map<string, ReservationGroup>();
    for (const row of rows) {
      const key = `${row.reservationId}#${row.reservDate}`;
      const group = grouped.get(key) ?? { reservationId: row.reservationId, reservDate: row.reservDate, slots: [] };
      group.slots.push(row);
      grouped.set(key, group);
    }
    return Array.from(grouped.values())
      .map(group => ({ ...group, slots: group.slots.sort((a, b) => a.slot.localeCompare(b.slot)) }));
  }, [rows]);
  const pending = !!created && !createdFound.current;
  const blocked = loading || loadError || cancelling || pending;

  const selectionValid = (group: ReservationGroup, at: number) => {
    const selected = selection?.reservationId === group.reservationId ? selection.slots : [];
    const suffix = cancelableSuffix(group, at);
    return selected.length > 0 && selected.length <= suffix.length
      && selected.every((slot, index) => slot === suffix[suffix.length - selected.length + index]);
  };
  const confirmingGroup = groups.find(group => group.reservationId === confirmingId);
  const confirmingSlots = confirmingGroup?.slots.filter(row => row.reservState === "신청") ?? [];
  const retained = confirmingSlots.filter(row => !selection?.slots.includes(row.slot));
  const cancelled = confirmingSlots.filter(row => selection?.slots.includes(row.slot));

  const cancel = async (group: ReservationGroup) => {
    if (blocked || cancelInFlight.current || !selectionValid(group, currentTime())) return;
    const slots = selection?.slots ?? [];
    cancelInFlight.current = true;
    setCancelling(true);
    setNotice(null);
    try {
      const res = await mainApi.cancel_table_tennis_reservation_slots({ reservationId: group.reservationId, userId, slots });
      if (!mounted.current) return;
      const result = res.response;
      if (res.code === "200" && result?.reservationId === group.reservationId
        && Array.isArray(result.cancelledSlots) && result.cancelledSlots.length === slots.length
        && slots.every(slot => result.cancelledSlots.includes(slot))) {
        const cancelled = confirmedCancellations.current.get(group.reservationId) ?? new Set<string>();
        slots.forEach(slot => cancelled.add(slot));
        confirmedCancellations.current.set(group.reservationId, cancelled);
        setRows(previous => previous.map(row => row.reservationId === group.reservationId && cancelled.has(row.slot)
          ? { ...row, reservState: "취소", communityPrice: 0 } : row));
        setNotice({ severity: "success", text: "선택한 시간이 취소되었습니다." });
      } else {
        const messages: Record<string, string> = {
          "301": "예약 상태가 변경되었습니다. 신청 상태를 다시 조회합니다.",
          "606": "이미 시작된 시간은 취소할 수 없습니다. 신청 상태를 다시 조회합니다.",
          "607": "마지막 예약 시간부터 연속해서만 취소할 수 있습니다. 신청 상태를 다시 조회합니다.",
          "403": "본인의 예약만 취소할 수 있습니다. 신청 상태를 다시 조회합니다.",
        };
        setNotice({ severity: "error", text: messages[res.code] || "예약을 취소하지 못했습니다. 신청 상태를 확인한 뒤 다시 시도해 주세요." });
        setReload(value => value + 1);
      }
    } catch {
      if (mounted.current) {
        setNotice({ severity: "error", text: "취소 결과를 확인하지 못했습니다. 신청 상태를 확인한 뒤 다시 시도해 주세요." });
        setReload(value => value + 1);
      }
    } finally {
      cancelInFlight.current = false;
      if (mounted.current) { setSelection(null); setExpandedId(""); setConfirmingId(""); setCancelling(false); }
    }
  };

  const isOngoing = (group: ReservationGroup) => group.slots.some(row => row.reservState === "신청"
    && startMs(group.reservDate, row.slot) <= now && startMs(group.reservDate, addHour(row.slot)) > now);
  const isCurrent = (group: ReservationGroup) => group.slots.some(row => row.reservState === "신청"
    && startMs(group.reservDate, addHour(row.slot)) > now);
  const firstStart = (group: ReservationGroup) => startMs(group.reservDate,
    (group.slots.find(row => row.reservState === "신청") ?? group.slots[0]).slot);
  const currentGroups = groups.filter(isCurrent).sort((a, b) =>
    Number(isOngoing(b)) - Number(isOngoing(a)) || firstStart(a) - firstStart(b));
  const historyGroups = groups.filter(group => !isCurrent(group)).sort((a, b) => firstStart(b) - firstStart(a));
  const kstDate = (ms: number) => new Date(ms + 9 * 3600000).toISOString().slice(0, 10).replace(/-/g, "");

  const renderGroup = (group: ReservationGroup) => {
    const key = `${group.reservationId}#${group.reservDate}`;
    const active = group.slots.filter(row => row.reservState === "신청");
    const cancelled = group.slots.filter(row => row.reservState === "취소");
    const suffix = cancelableSuffix(group, now);
    const expanded = expandedId === group.reservationId;
    const selected = selection?.reservationId === group.reservationId ? selection.slots : [];
    const canCancelSelection = expanded && selectionValid(group, now);
    const remaining = active.filter(row => !selected.includes(row.slot));
    const weekday = new Date(startMs(group.reservDate, "12:00")).toLocaleDateString("ko-KR", { weekday: "short", timeZone: "Asia/Seoul" });
    const date = `${group.reservDate.slice(0, 4) !== kstDate(now).slice(0, 4) ? `${group.reservDate.slice(0, 4)}년 ` : ""}${Number(group.reservDate.slice(4, 6))}월 ${Number(group.reservDate.slice(6, 8))}일 (${weekday})`;
    const relativeDate = group.reservDate === kstDate(now) ? "오늘" : group.reservDate === kstDate(now + 86400000) ? "내일" : "";
    const status = !active.length ? "취소 완료" : isOngoing(group) ? "이용 중" : isCurrent(group) ? "예약 확정" : "이용 완료";
    return (
      <article className={`hp-book-cafe-booking${isOngoing(group) ? " hp-book-cafe-booking--ongoing" : ""}`} key={key}
        aria-label={`${dateLabel(group.reservDate)} 탁구장 예약 ${timeRange(active.length ? active : group.slots)}`}>
        <div className="hp-book-cafe-booking__heading">
          <h2>{date}{relativeDate ? <small>{relativeDate}</small> : null}</h2>
          <span className="hp-book-cafe-booking__status">{status}</span>
        </div>
        <p className="hp-book-cafe-booking__time">{timeRange(active.length ? active : group.slots)}</p>
        <p className="hp-book-cafe-booking__duration">{active.length ? `총 ${active.length}시간 이용` : "예약이 취소되었습니다"}</p>
        <div className="hp-book-cafe-booking__footer">
          <p><span>{active.length ? "청구 예정" : "청구 금액"}</span><strong>{totalPrice(active)}원</strong></p>
          {suffix.length > 0 || expanded ? (
            <Button variant="text" disabled={blocked} aria-expanded={expanded} aria-controls={`cancel-${key}`}
              onClick={() => { setExpandedId(expanded ? "" : group.reservationId); setSelection(null); }}>
              {expanded ? "취소 닫기" : "예약 취소"}
            </Button>
          ) : null}
        </div>
        {cancelled.length > 0 && active.length > 0 ? (
          <details className="hp-book-cafe-booking__cancelled"><summary>취소 내역 {cancelled.length}시간</summary><p>{timeRange(cancelled)} · 취소 완료</p></details>
        ) : null}
        {expanded ? (
          <section className="hp-book-cafe-booking__cancel" id={`cancel-${key}`} aria-label="예약 취소 설정">
            <h3>취소할 시간을 선택해 주세요</h3>
            <p>아직 시작하지 않은 마지막 시간부터 1시간 단위로 취소할 수 있습니다.</p>
            <div role="radiogroup" aria-label="취소할 시간">
              {suffix.map((_, index) => {
                const hours = index + 1;
                const slots = suffix.slice(-hours);
                const label = `${hours === active.length ? "전체" : "마지막"} ${hours}시간 취소`;
                const optionRows = active.filter(row => slots.includes(row.slot));
                return <label className="hp-book-cafe-booking__option" key={hours}>
                  <input type="radio" name={`cancel-${key}`} checked={canCancelSelection && selected.length === hours} disabled={blocked}
                    onChange={() => setSelection({ reservationId: group.reservationId, slots })} />
                  <span><strong>{label}</strong><span>{timeRange(optionRows)}</span></span>
                </label>;
              })}
            </div>
            {selected.length > 0 && !canCancelSelection ? <p role="status">이용시간이 시작되었습니다. 취소할 시간을 다시 선택해 주세요.</p> : null}
            {canCancelSelection ? (
              <section className="hp-book-cafe-booking__preview" aria-label="취소 후 예약 확인">
                <dl>
                  <div><dt>취소할 시간</dt><dd>{timeRange(active.filter(row => selected.includes(row.slot)))}</dd></div>
                  <div><dt>남는 예약</dt><dd>{remaining.length ? `${timeRange(remaining)} · ${remaining.length}시간` : "없음"}</dd></div>
                  <div><dt>취소 후 청구 예정</dt><dd>{totalPrice(remaining)}원</dd></div>
                </dl>
              </section>
            ) : null}
            <Button variant="contained" color="error" disabled={blocked || !canCancelSelection} onClick={() => setConfirmingId(group.reservationId)}>
              {cancelling ? "취소 처리 중…" : "취소 확정"}
            </Button>
          </section>
        ) : null}
      </article>
    );
  };

  return (
    <main className="hpoint-renewal hp-reservation-page hp-book-cafe-bookings hp-table-tennis">
      <header><h1 className="hp-page-title">탁구장 예약 내역</h1><p className="hp-page-description">예약한 날짜와 이용시간을 확인하세요.</p></header>
      <Button variant="outlined" disabled={loading || cancelling} onClick={() => setReload(value => value + 1)}>
        {loadError ? "다시 조회" : "새로고침"}
      </Button>
      {pending && <Alert severity="info" role="status">예약이 완료되었습니다. 신청 내역에 반영 중입니다. 잠시 후 새로고침해 주세요.</Alert>}
      {notice && <Alert severity={notice.severity} sx={{ color: "text.primary" }}>{notice.text}</Alert>}
      {loadError && <Alert severity="error" sx={{ color: "text.primary" }}>신청 상태를 불러오지 못했습니다. 다시 조회해 주세요.</Alert>}
      {loading && <div className="hp-book-cafe-bookings__loading"><CircularProgress aria-label="신청 상태 조회 중" /></div>}
      {!loading && !loadError && !pending && groups.length === 0 && <Alert severity="info">예약된 내용이 없습니다.</Alert>}
      {currentGroups.length ? <section className="hp-book-cafe-bookings__list" aria-label="이용 중 · 예정 예약">{currentGroups.map(renderGroup)}</section> : null}
      {historyGroups.length ? <details className="hp-book-cafe-bookings__history"><summary><span>지난 예약 · 취소 내역</span><span>{historyGroups.length}건</span></summary><div className="hp-book-cafe-bookings__list">{historyGroups.map(renderGroup)}</div></details> : null}
      <Dialog open={Boolean(confirmingGroup)} onClose={() => { if (!cancelling) setConfirmingId(""); }}
        fullWidth maxWidth="xs" aria-labelledby="table-tennis-cancel-title" aria-describedby="table-tennis-cancel-description"
        PaperProps={{ className: "hpoint-renewal hp-book-cafe-cancel-dialog hp-table-tennis" }}>
        <DialogTitle id="table-tennis-cancel-title">예약을 취소하시겠어요?</DialogTitle>
        <DialogContent>
          <Typography id="table-tennis-cancel-description" className="hp-table-tennis__cancel-date">{confirmingGroup ? dateLabel(confirmingGroup.reservDate) : ""}</Typography>
          <section className="hp-book-cafe-cancel-dialog__available" aria-label="사용가능시간">
            <span>사용가능시간</span><strong>{retained.length ? timeRange(retained).replace(/ — /g, " ~ ") : "없음"}</strong>
            <small>{retained.length ? `취소 후에도 ${retained.length}시간 이용할 수 있어요.` : "예약이 전체 취소됩니다."}</small>
          </section>
          <section className="hp-book-cafe-cancel-dialog__cancelled" aria-label="취소시간">
            <span>취소시간</span><strong>{timeRange(cancelled).replace(/ — /g, " ~ ")}</strong>
          </section>
          {confirmingGroup && !selectionValid(confirmingGroup, now) && <Alert severity="warning">이용시간이 시작되었습니다. 돌아가서 취소할 시간을 다시 선택해 주세요.</Alert>}
        </DialogContent>
        <DialogActions>
          <Button disabled={cancelling} onClick={() => setConfirmingId("")}>돌아가기</Button>
          <Button variant="contained" color="error" disabled={blocked || !confirmingGroup || !selectionValid(confirmingGroup, now)}
            onClick={() => { if (confirmingGroup) void cancel(confirmingGroup); }}>{cancelling ? "취소 처리 중…" : "예약 취소하기"}</Button>
        </DialogActions>
      </Dialog>
    </main>
  );
};

// A user change discards the previous user's rows, pending requests and cancellation acknowledgements.
const ReservationTableTennisEnd = ({ userState }: { userState: userState }) =>
  <ReservationHistory key={userState.userId} userId={userState.userId} />;

export default ReservationTableTennisEnd;
